import { EditPencil as EditPencilIcon } from 'iconoir-react'
import { ReactElement, useState } from 'react'
import ComboAsync from 'app/components/ui/combo/combo-async'
import { StaffForm } from 'app/components/staff/StaffForm'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import type { ActivityJson, StaffJson } from 'app/types/entities'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { showModal } from 'app/lib/modal'
import { OptionType } from 'types/OptionType'

type StaffGql = { id: string; name: string; userId?: string }

type Props = {
  staff: ActivityJson[`owner`]
  locationId?: string
  workspaceId?: string
  onChange: (staff: ActivityJson[`owner`]) => void
}
const borderClassName = `border border-strong`
const backgroundClassName = `bg-surface-strong`
const textClassName = `text-strong font-bold`
/**
 * Renders StaffAssign component.
 * @returns ReactElement
 */
export function StaffAssign({ staff, locationId, workspaceId, onChange }: Props): ReactElement {
  const [currentStaff, setCurrentStaff] = useState<ActivityJson[`owner`]>(staff)
  // Staff created inline from this dropdown. The staffMembers search index lags
  // creation by a few seconds, so a refetch alone would not list the new record.
  // ComboAsync merges additionalChoices into every result and dedupes by id.
  const [createdStaffOptions, setCreatedStaffOptions] = useState<OptionType[]>([])
  const checkPermission = useCheckPermission()
  const canCreateStaff = checkPermission([`staff:manage`])

  const selectCreatedStaff = (created?: Partial<StaffJson>) => {
    if (!created?.id || !created?.name) return
    const owner = { id: created.id, name: created.name }
    const option: OptionType = {
      id: owner.name,
      name: owner.name,
      value: owner,
      meta: { nameInitial: true, borderClassName, backgroundClassName, textClassName },
    }
    setCurrentStaff(owner)
    setCreatedStaffOptions((options) => [...options.filter((o) => o.id !== option.id), option])
    onChange(owner)
  }

  const openStaffModal = () => {
    showModal(<StaffForm onSubmit={(data) => selectCreatedStaff(data?.staff)} />, {
      title: `Add Staff`,
      isFullScreen: false,
      removePadding: true,
      useRadix: true,
    })
  }

  const fetcher = async (params: { query: string; [key: string]: string }) => {
    const result = await gqlFetchList<StaffGql>(
      `staffMembers`,
      {
        search: params.query || undefined,
        pagination: { limit: 25 },
      },
      `id name userId`,
    )
    return result
  }

  return (
    <div>
      <span className="w-full">
        <ComboAsync
          placeholder=""
          customButton={
            <button type="button" className="flex flex-col gap-y-1">
              <div className="flex items-center gap-x-1 text-body-xs text-subtle">
                <span>Staff</span>
                <div className="p-0.5">
                  <EditPencilIcon className="h-3 w-3" />
                </div>
              </div>
              <span className="mt-0.5 wrap-break-words text-left text-ui-xs capitalize text-strong">
                {currentStaff?.name}
              </span>
            </button>
          }
          inputClassName="h-full! border-0! shadow-none! py-0! pl-0! pr-0! cursor-pointer"
          chevronClassName="hidden group-hover/cell:block"
          value={
            currentStaff
              ? {
                  name: currentStaff?.name,
                  id: currentStaff?.id ?? currentStaff?.name,
                  value: currentStaff?.name,
                }
              : { name: ` `, id: ``, value: `` }
          }
          fetcher={fetcher}
          optionsMapper={(res) => {
            return res?.hits?.map((hit: StaffGql) => ({
              id: hit.name,
              name: hit.name,
              value: { id: hit.id, name: hit.name },
              meta: {
                nameInitial: true,
                borderClassName,
                backgroundClassName,
                textClassName,
              },
            }))
          }}
          additionalChoices={createdStaffOptions}
          additionalParams={{
            locationId: locationId!,
            workspaceId: workspaceId!,
          }}
          onChange={(val) => {
            if (val?.value && val?.value?.id !== currentStaff?.id) {
              setCurrentStaff({ id: val?.value?.id, name: val?.value?.name })
              onChange(val?.value)
            } else {
              setCurrentStaff(undefined)
              onChange(undefined)
            }
          }}
          addText="Add Staff"
          onAddNewValue={canCreateStaff ? openStaffModal : undefined}
          allowDeselectOption
          showSearchInDropdown
        />
      </span>
    </div>
  )
}
